<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import type { ChatbotConfig, UIMessage } from './types'
import { getSessionId } from './session'
import { checkWhatsapp, escalate, streamMessage } from './api'
import { renderMarkdown } from './markdown'
import { widgetStrings } from './i18n'
import { OVERLAY_SELECTOR } from './styles'

interface Props {
  config: ChatbotConfig
  origin: string
  productSlug: string
}

const props = defineProps<Props>()

// Resolved once: language is a property of the bot, not of the visitor.
const t = widgetStrings(props.config.language)

const sessionId = getSessionId(props.productSlug)
const STORAGE_KEY = `cc-chatbot-msgs-${props.productSlug}-${sessionId}`
const PULSE_KEY = `cc-chatbot-pulsed-${props.productSlug}`

const isOpen = ref(false)
const isClosing = ref(false)
const view = ref<'chat' | 'escalate' | 'success'>('chat')
const messages = ref<UIMessage[]>([])
const input = ref('')
const isStreaming = ref(false)
const error = ref<string | null>(null)
const ticketId = ref<string | null>(null)
const isEscalating = ref(false)
const messagesEl = ref<HTMLElement | null>(null)
const inputEl = ref<HTMLTextAreaElement | null>(null)
const shouldPulse = ref(false)
const keyboardPx = ref(0)

// Closed-bubble icon rotation. When the product has WhatsApp enabled the bubble
// alternates between the chat icon and the WhatsApp one so the visitor sees
// both entry points without a second button.
const BUBBLE_ICON_MS = 4000
const bubbleIcon = ref<'chat' | 'whatsapp'>('chat')
let bubbleIconTimer: ReturnType<typeof setInterval> | null = null

const isMobileMq = typeof window !== 'undefined' ? window.matchMedia('(max-width: 640px)') : null
const isMobile = ref(isMobileMq?.matches ?? false)
function onMqChange(e: MediaQueryListEvent) { isMobile.value = e.matches }

// Anything under this is browser chrome (the collapsing URL bar), not a keyboard.
const KEYBOARD_MIN_PX = 90

/**
 * Height of the on-screen keyboard, in layout CSS pixels.
 *
 * `visualViewport.height` is expressed in *visual* pixels, so a pinch-zoom of
 * 2x halves it even though no keyboard is open. Multiplying by `scale` brings
 * it back to layout pixels and makes the reading zoom-proof — without that,
 * a zoomed page reported a keyboard several hundred pixels tall and the panel
 * shrank to nothing. `offsetTop` covers the case where the browser scrolls the
 * visual viewport down to reveal the focused field.
 */
function readKeyboardPx(): number {
  const vv = window.visualViewport
  if (!vv) return 0
  const scale = vv.scale || 1
  const inset = window.innerHeight - vv.offsetTop - vv.height * scale
  if (!Number.isFinite(inset) || inset < KEYBOARD_MIN_PX) return 0
  // Never claim more than half the screen: the panel keeps a usable height
  // even if a browser reports something unexpected.
  return Math.round(Math.min(inset, window.innerHeight * 0.5))
}

// CSS does the heavy lifting — JS only exposes the keyboard height (--cc-kb)
// and the room kept for the bubble (--cc-gap, which collapses once the bubble
// is hidden behind the keyboard). The .cc-panel mobile rules animate off both.
const panelStyle = computed<Record<string, string> | undefined>(() => {
  if (!isMobile.value) return undefined
  const kb = keyboardPx.value
  return {
    '--cc-kb': `${kb}px`,
    '--cc-gap': kb > 0 ? '12px' : '72px',
  }
})

function onViewportResize() {
  const next = readKeyboardPx()
  if (next === keyboardPx.value) return
  keyboardPx.value = next
  // Keep the latest messages above the keyboard when it opens.
  if (isMobile.value && isOpen.value) void scrollToBottom()
}

const escalateForm = reactive<{ email: string, name: string, subject: string }>({
  email: '',
  name: '',
  subject: '',
})

const currentAssistantMessage = computed(() => {
  for (let i = messages.value.length - 1; i >= 0; i--) {
    const m = messages.value[i]
    if (m.role === 'assistant') return m
    if (m.role === 'user') return null
  }
  return null
})

const welcomeText = computed(() => {
  const configured = props.config.welcomeMessage?.trim()
  if (configured && configured.length > 0) return configured
  return t.welcomeFallback
})

// Only a flag from the config endpoint — the raw number is never sent until
// checkWhatsapp() clears the rate-limit/hours gate below, so an anonymous GET
// against the config endpoint can't be used to harvest it.
const hasWhatsapp = computed(() => props.config.whatsappEnabled)

function buildWhatsappHref(rawNumber: string): string | null {
  const digits = rawNumber.trim().replace(/\D+/g, '')
  if (digits.length < 8) return null
  const brand = props.config.brandName || t.defaultBrand
  const text = encodeURIComponent(t.whatsappMessage(brand))
  return `https://wa.me/${digits}?text=${text}`
}

const whatsappChecking = ref(false)
const whatsappBlockedMsg = ref<string | null>(null)

async function onWhatsappClick() {
  if (whatsappChecking.value || !hasWhatsapp.value) return
  whatsappChecking.value = true
  whatsappBlockedMsg.value = null
  try {
    const result = await checkWhatsapp({
      origin: props.origin,
      productSlug: props.productSlug,
      sessionId,
    })
    const href = result.allowed && result.whatsappNumber ? buildWhatsappHref(result.whatsappNumber) : null
    if (href) {
      window.open(href, '_blank', 'noopener')
    }
    else {
      whatsappBlockedMsg.value = result.reason === 'rate_limited' ? t.whatsappRateLimited : t.whatsappOutsideHours
    }
  }
  finally {
    whatsappChecking.value = false
  }
}

function loadMessages(): UIMessage[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as UIMessage[]
    return Array.isArray(parsed) ? parsed : []
  }
  catch {
    return []
  }
}

function persistMessages() {
  try {
    // Strip the system welcome so it's always fresh; cap at 50 most recent.
    const toStore = messages.value
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .slice(-50)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore))
  }
  catch { /* storage quota or privacy mode — ignore */ }
}

function onKeydown(e: KeyboardEvent) {
  if (e.key !== 'Escape' || !isOpen.value) return
  // A host dialog is covering us, so this Escape is meant for it, not for us.
  // main.ts hides the widget on the same signal.
  if (document.querySelector(OVERLAY_SELECTOR)) return
  // No preventDefault. This listener sits on window and registers at mount,
  // before any host dialog's own window listener. Reka skips its dismiss when
  // defaultPrevented is set, so cancelling here stopped Escape from closing
  // the host's modal.
  closePanel()
}

onMounted(() => {
  const restored = loadMessages()
  messages.value = [
    { role: 'system', content: welcomeText.value },
    ...restored,
  ]

  const hint = typeof window !== 'undefined' ? window.__ccChatbotUser : undefined
  if (hint?.email) escalateForm.email = hint.email
  if (hint?.name) escalateForm.name = hint.name

  // Only pulse the first time this visitor sees the bubble.
  try {
    if (!localStorage.getItem(PULSE_KEY)) {
      shouldPulse.value = true
      localStorage.setItem(PULSE_KEY, '1')
    }
  }
  catch { /* ignore */ }

  window.addEventListener('keydown', onKeydown)
  isMobileMq?.addEventListener('change', onMqChange)
  window.visualViewport?.addEventListener('resize', onViewportResize)
  window.visualViewport?.addEventListener('scroll', onViewportResize)
  onViewportResize()

  if (hasWhatsapp.value) {
    bubbleIconTimer = setInterval(() => {
      // Only rotate while the bubble is closed — open, it shows the X.
      if (isOpen.value) return
      bubbleIcon.value = bubbleIcon.value === 'chat' ? 'whatsapp' : 'chat'
    }, BUBBLE_ICON_MS)
  }
})

onBeforeUnmount(() => {
  if (bubbleIconTimer) clearInterval(bubbleIconTimer)
  window.removeEventListener('keydown', onKeydown)
  isMobileMq?.removeEventListener('change', onMqChange)
  window.visualViewport?.removeEventListener('resize', onViewportResize)
  window.visualViewport?.removeEventListener('scroll', onViewportResize)
})

watch(messages, () => {
  void scrollToBottom()
  persistMessages()
}, { deep: true })

watch(view, () => void scrollToBottom())
watch(isOpen, async (open) => {
  if (!open) bubbleIcon.value = 'chat'
  if (open) {
    await nextTick()
    // On mobile, don't auto-focus — it pops the keyboard before the user opts in.
    if (!isMobile.value) inputEl.value?.focus()
    void scrollToBottom()
  }
})

async function scrollToBottom() {
  await nextTick()
  const el = messagesEl.value
  if (el) el.scrollTop = el.scrollHeight
}

function closePanel() {
  isClosing.value = true
  setTimeout(() => {
    isOpen.value = false
    isClosing.value = false
  }, 160)
}

function resetConversation() {
  if (isStreaming.value) return
  if (!confirm(t.resetConfirm)) return
  messages.value = [{ role: 'system', content: welcomeText.value }]
  error.value = null
  view.value = 'chat'
  try { localStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
}

async function onSend() {
  const text = input.value.trim()
  if (!text || isStreaming.value) return

  error.value = null
  input.value = ''
  messages.value.push({ role: 'user', content: text })

  const payload: UIMessage[] = messages.value
    .filter(m => m.role === 'user' || m.role === 'assistant')
    .map(m => ({ role: m.role, content: m.content }))

  const assistantIdx = messages.value.push({ role: 'assistant', content: '' }) - 1
  isStreaming.value = true

  try {
    const stream = streamMessage({
      origin: props.origin,
      productSlug: props.productSlug,
      sessionId,
      messages: payload,
      pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
      user: typeof window !== 'undefined' ? window.__ccChatbotUser : undefined,
    })

    for await (const chunk of stream) {
      if (chunk.type === 'error') {
        error.value = chunk.message || 'Error'
        break
      }
      const current = messages.value[assistantIdx]
      if (current) current.content += chunk.text
    }

    const last = messages.value[assistantIdx]
    if (last && last.role === 'assistant' && last.content.length === 0) {
      messages.value.splice(assistantIdx, 1)
    }
  }
  catch {
    // Never surface a raw error to the visitor — the stream layer already maps
    // known statuses; this is the network/unknown fallback.
    error.value = t.genericError
    const last = messages.value[assistantIdx]
    if (last && last.role === 'assistant' && last.content.length === 0) {
      messages.value.splice(assistantIdx, 1)
    }
  }
  finally {
    isStreaming.value = false
    await nextTick()
    if (!isMobile.value) inputEl.value?.focus()
  }
}

async function onEscalate() {
  if (!escalateForm.email || isEscalating.value) return
  error.value = null
  isEscalating.value = true
  try {
    const res = await escalate({
      origin: props.origin,
      productSlug: props.productSlug,
      sessionId,
      contact: {
        email: escalateForm.email,
        name: escalateForm.name || undefined,
      },
      subject: escalateForm.subject || undefined,
    })
    ticketId.value = res.ticketId
    view.value = 'success'
  }
  catch (err) {
    error.value = err instanceof Error ? err.message : t.escalateError
  }
  finally {
    isEscalating.value = false
  }
}

function openPanel() {
  isOpen.value = true
}

function toggleBubble() {
  if (isOpen.value) closePanel()
  else openPanel()
}
</script>

<template>
  <button
    :class="['cc-bubble', { 'cc-bubble-pulse': shouldPulse && !isOpen, 'cc-bubble-open': isOpen }]"
    :aria-label="isOpen ? t.closeChat : t.openChat"
    @click="toggleBubble"
  >
    <span v-if="!isOpen" class="cc-bubble-label">{{ t.bubbleLabel }}</span>
    <!-- The key forces a re-mount on every swap so cc-bubble-icon-in replays. -->
    <svg
      v-if="!isOpen && bubbleIcon === 'whatsapp'"
      key="whatsapp"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M20.52 3.48A11.88 11.88 0 0 0 12.06 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.14 1.6 5.95L0 24l6.33-1.66a11.86 11.86 0 0 0 5.73 1.46h.01c6.56 0 11.9-5.34 11.9-11.9a11.83 11.83 0 0 0-3.46-8.42ZM12.07 21.8a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.76.99 1-3.67-.23-.38a9.85 9.85 0 0 1-1.51-5.25c0-5.46 4.44-9.9 9.9-9.9a9.83 9.83 0 0 1 7 2.9 9.83 9.83 0 0 1 2.9 7c0 5.46-4.44 9.9-9.9 9.9Zm5.43-7.41c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.04-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.6-.92-2.2-.24-.58-.48-.5-.67-.51l-.57-.01a1.1 1.1 0 0 0-.8.37c-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.11 3.22 5.12 4.51.72.31 1.28.49 1.72.63.72.23 1.37.2 1.89.12.58-.09 1.76-.72 2.01-1.42.25-.7.25-1.3.17-1.42-.07-.13-.27-.2-.57-.35Z" />
    </svg>
    <svg
      v-else-if="!isOpen"
      key="chat"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
    <svg
      v-else
      key="close"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2.5"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  </button>

  <div
    v-if="isOpen"
    :class="['cc-panel', { 'cc-panel-closing': isClosing }]"
    :style="panelStyle"
    role="dialog"
    :aria-label="t.dialogLabel"
  >
    <header class="cc-header">
      <div class="cc-title-wrap">
        <div class="cc-avatar" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </div>
        <div class="cc-title-col">
          <span class="cc-title">{{ config.brandName }}</span>
          <span class="cc-subtitle"><span class="cc-status-dot" /> {{ t.online }}</span>
        </div>
      </div>
      <div class="cc-header-actions">
        <button
          v-if="view === 'chat'"
          class="cc-escalate"
          type="button"
          @click="view = 'escalate'"
        >
          {{ t.talkToPerson }}
        </button>
        <button
          class="cc-close"
          type="button"
          :aria-label="t.closeChat"
          @click="closePanel"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </header>

    <main
      v-if="view === 'chat'"
      ref="messagesEl"
      class="cc-messages"
    >
      <template v-for="(m, i) in messages" :key="i">
        <!-- Assistant text is markdown-rendered (bold, lists, links) through a
             safe escape-first renderer; user text stays plain to avoid any
             echo surface. -->
        <div
          v-if="m.role === 'assistant' && m.content.length > 0"
          class="cc-message assistant"
          v-html="renderMarkdown(m.content)"
        />
        <div
          v-else-if="m.role !== 'assistant'"
          :class="['cc-message', m.role]"
        >
          {{ m.content }}
        </div>
      </template>
      <div
        v-if="isStreaming && (!currentAssistantMessage || currentAssistantMessage.content.length === 0)"
        class="cc-typing"
        :aria-label="t.typing"
      >
        <span class="cc-typing-dot" />
        <span class="cc-typing-dot" />
        <span class="cc-typing-dot" />
      </div>
    </main>

    <div v-if="error" class="cc-error">
      {{ error }}
    </div>

    <div v-if="view === 'chat'" class="cc-input-bar">
      <textarea
        ref="inputEl"
        v-model="input"
        class="cc-input"
        :placeholder="t.inputPlaceholder"
        rows="1"
        :disabled="isStreaming"
        @keydown.enter.exact.prevent="onSend"
      />
      <button
        class="cc-send"
        type="button"
        :aria-label="t.send"
        :disabled="!input.trim() || isStreaming"
        @click="onSend"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <line x1="12" y1="19" x2="12" y2="5" />
          <polyline points="5 12 12 5 19 12" />
        </svg>
      </button>
    </div>

    <form
      v-else-if="view === 'escalate'"
      class="cc-escalate-form"
      @submit.prevent="onEscalate"
    >
      <h3>{{ t.talkToPersonTitle }}</h3>
      <p>{{ t.escalateIntro }}</p>
      <button
        v-if="hasWhatsapp"
        type="button"
        :disabled="whatsappChecking"
        class="cc-whatsapp"
        @click="onWhatsappClick"
      >
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M20.52 3.48A11.88 11.88 0 0 0 12.06 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.14 1.6 5.95L0 24l6.33-1.66a11.86 11.86 0 0 0 5.73 1.46h.01c6.56 0 11.9-5.34 11.9-11.9a11.83 11.83 0 0 0-3.46-8.42ZM12.07 21.8a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.76.99 1-3.67-.23-.38a9.85 9.85 0 0 1-1.51-5.25c0-5.46 4.44-9.9 9.9-9.9a9.83 9.83 0 0 1 7 2.9 9.83 9.83 0 0 1 2.9 7c0 5.46-4.44 9.9-9.9 9.9Zm5.43-7.41c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.04-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.6-.92-2.2-.24-.58-.48-.5-.67-.51l-.57-.01a1.1 1.1 0 0 0-.8.37c-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.11 3.22 5.12 4.51.72.31 1.28.49 1.72.63.72.23 1.37.2 1.89.12.58-.09 1.76-.72 2.01-1.42.25-.7.25-1.3.17-1.42-.07-.13-.27-.2-.57-.35Z"/>
        </svg>
        <span>{{ t.whatsappCta }}</span>
      </button>
      <p v-if="whatsappBlockedMsg" class="cc-whatsapp-blocked">
        {{ whatsappBlockedMsg }}
      </p>
      <label>
        {{ t.email }}
        <input
          v-model="escalateForm.email"
          type="email"
          required
          autocomplete="email"
          :placeholder="t.emailPlaceholder"
        >
      </label>
      <label>
        {{ t.nameOptional }}
        <input
          v-model="escalateForm.name"
          type="text"
          autocomplete="name"
          :placeholder="t.namePlaceholder"
        >
      </label>
      <label>
        {{ t.messageOptional }}
        <textarea
          v-model="escalateForm.subject"
          rows="3"
          :placeholder="t.messagePlaceholder"
        />
      </label>
      <div class="cc-actions">
        <button
          type="button"
          class="cc-cancel"
          @click="view = 'chat'"
        >
          {{ t.cancel }}
        </button>
        <button
          type="submit"
          class="cc-submit"
          :disabled="!escalateForm.email || isEscalating"
        >
          {{ isEscalating ? t.submitting : t.submit }}
        </button>
      </div>
    </form>

    <div v-else class="cc-success">
      <div class="cc-icon" aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h3>{{ t.successTitle }}</h3>
      <p>{{ t.successBody }}</p>
    </div>

    <footer class="cc-footer-brand">
      <button
        v-if="view === 'chat' && messages.length > 1"
        class="cc-reset"
        type="button"
        :disabled="isStreaming"
        @click="resetConversation"
      >
        {{ t.reset }}
      </button>
      <span v-else />
      <a href="https://codecave.ar" target="_blank" rel="noopener">powered by codeCave</a>
    </footer>
  </div>
</template>
